import React from 'react';
import { Box, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem, IconButton, Chip, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import * as MuiIcons from '@mui/icons-material';
import AgentCardsFilter from '../../pages/userAgent/agentMarketPlace/AgentCardsFilter';
import { SortAgentsField, SortAgentsFieldsOrder } from '../../pages/userAgent/agentMarketPlace/AgentMarketPlace';

interface AgentFilterBarProps {
  agentCardsFilterSections: any[];
  filteredAgentList: any[];
  agentListLoading: boolean;
  filteredAgentsCount: number;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  sortAgentsField: SortAgentsField | '';
  setSortAgentsField: (field: SortAgentsField | '') => void;
  sortAgentFieldsOrder: SortAgentsFieldsOrder;
  handleToggleAgentsSortOrder: () => void;
  isMobile: boolean;
  getAllSelectedFilters: () => any[];
  removeFilter: (filterValue: string, filterType: string) => void;
  hasSelectedFilters: boolean;
  clearAgentFilters: () => void;
  setPage: (page: number) => void;
}

const AgentFilterBar: React.FC<AgentFilterBarProps> = ({
  agentCardsFilterSections,
  filteredAgentList,
  agentListLoading,
  filteredAgentsCount,
  searchTerm,
  setSearchTerm,
  sortAgentsField,
  setSortAgentsField,
  sortAgentFieldsOrder,
  handleToggleAgentsSortOrder,
  getAllSelectedFilters,
  removeFilter,
  hasSelectedFilters,
  clearAgentFilters,
  setPage,
}) => {
  return (
    <Box className="agent-filter-controls-container" sx={{ width: '100%', mb: 3 }}>
      <Box className="agent-filter-controls-row" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
        <AgentCardsFilter
          agentCardsFilterSections={agentCardsFilterSections}
          agentList={filteredAgentList}
          agentListLoading={agentListLoading}
          onPageChange={setPage}
        />
        <TextField
          className="agent-search-field"
          label={
            <Box component="span">
              Search{' '}
              <Box component="span" sx={{ fontWeight: 'bold', display: 'inline' }}>{filteredAgentsCount}</Box>{' '}agents by Name or Owner
            </Box>
          }
          variant="outlined"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{ endAdornment: (<InputAdornment position="end"><SearchIcon sx={{ color: 'action.active' }} /></InputAdornment>) }}
          sx={{ minWidth: 300, '& .MuiInputBase-root': { height: '50px', display: 'flex', alignItems: 'center', borderRadius: '8px' }, '& .MuiInputLabel-root': { transform: 'translate(14px, 13px) scale(1)', maxWidth: 'calc(100% - 45px)', '&.MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(0.75)', maxWidth: 'none' } } }}
        />
        <Box className="agent-sort-controls" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl variant="outlined" className="sort-select" sx={{ minWidth: 150 }}>
            <InputLabel id="sort-select-label">Sort By</InputLabel>
            <Select
              labelId="sort-select-label"
              id="sort-select"
              value={sortAgentsField}
              onChange={e => setSortAgentsField(e.target.value as SortAgentsField | '')}
              label="Sort By"
              startAdornment={<MuiIcons.Sort sx={{ ml: 1, mr: 0.5, color: 'action.active' }} />}
              sx={{ height: '50px', borderRadius: '8px' }}
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem key={SortAgentsField.Category} value={SortAgentsField.Category}>Domain / Industry</MenuItem>
              <MenuItem key={SortAgentsField.Name} value={SortAgentsField.Name}>Name</MenuItem>
              <MenuItem key={SortAgentsField.Owner} value={SortAgentsField.Owner}>Owner</MenuItem>
            </Select>
          </FormControl>
          <IconButton
            onClick={handleToggleAgentsSortOrder}
            disabled={!sortAgentsField}
            title={`Sort ${sortAgentFieldsOrder === SortAgentsFieldsOrder.Ascending ? 'Ascending' : 'Descending'}`}
            sx={{ height: '50px' }}
          >
            {sortAgentFieldsOrder === SortAgentsFieldsOrder.Ascending ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
          </IconButton>
        </Box>
      </Box>
      {(hasSelectedFilters || getAllSelectedFilters().length > 0) && (
        <Box className="agent-active-filters-row" sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {getAllSelectedFilters().length > 0 && (
            <Box className="selected-filters-chips" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {getAllSelectedFilters().map((filter, index) => (
                <Chip
                  key={`${filter.type}-${filter.value}-${index}`}
                  label={`${filter.value}`}
                  variant="filled"
                  color="primary"
                  size="small"
                  onDelete={() => removeFilter(filter.value, filter.type)}
                  sx={{ '& .MuiChip-deleteIcon': { color: 'inherit' } }}
                />
              ))}
            </Box>
          )}
          {hasSelectedFilters && (
            <Button className="clear-all-button" color="error" size="large" onClick={clearAgentFilters} sx={{ textTransform: 'none', height: '40px', borderRadius: '8px', whiteSpace: 'nowrap', px: 2 }}>
              Clear All
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};
export default AgentFilterBar;
